import { useCallback, useRef } from 'react'

import {
  MeetingsMeetingProviderItemPageFragment,
  MeetingsMeetingProviderUpdatePageContentDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

interface UseAutosaveReturn {
  isSavingNotes: boolean
  setNotes: (json: string) => void
  getCleanup: () => () => void
  error: string | null
}

interface UseAutosaveProps {
  page: MeetingsMeetingProviderItemPageFragment | null
}

export const useAutosave = ({ page }: UseAutosaveProps): UseAutosaveReturn => {
  const autosaveTimeoutRef = useRef<NodeJS.Timeout>()

  // Mutation for updating notes
  const [updateNotes, { loading: isSavingNotes, error }] = useMutation(
    MeetingsMeetingProviderUpdatePageContentDocument,
    {
      extractorFn: (data) => data.pagesMutations.updatePagesPage,
    }
  )

  const handleAutosave = useCallback(
    async (notesContent: string) => {
      if (!page?.id) {
        return
      }

      await updateNotes({
        variables: {
          content: notesContent,
          id: page.id,
        },
      })
    },
    [updateNotes, page?.id]
  )

  const debouncedAutosave = useCallback(
    (notesContent: string) => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current)
      }
      autosaveTimeoutRef.current = setTimeout(() => {
        handleAutosave(notesContent)
      }, 1500) // Wait 1.5 second after the last change
    },
    [handleAutosave]
  )

  const setNotes = useCallback(
    (json: string) => {
      try {
        JSON.parse(json)
        debouncedAutosave(json)
      } catch (e) {
        // Invalid JSON, don't save
      }
    },
    [debouncedAutosave]
  )

  // Cleanup timeout on unmount
  const cleanup = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
    }
  }, [])

  // Return cleanup function to be called by the component
  const getCleanup = useCallback(() => cleanup, [cleanup])

  return {
    isSavingNotes,
    setNotes,
    getCleanup,
    error: error?.message ?? null,
  }
}
